import React, { useState } from 'react';
import { 
  EnvelopeIcon, 
  MapPinIcon, 
  ClockIcon,
  ChatBubbleLeftRightIcon,
  AcademicCapIcon,
  CogIcon,
  PhotoIcon
} from '@heroicons/react/24/outline';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Handle form submission here
    console.log('Form submitted:', formData);
    // Reset form
    setFormData({
      name: '',
      email: '',
      subject: '',
      message: ''
    });
  };

  const contactInfo = [
    {
      icon: EnvelopeIcon,
      title: "Email Support",
      details: "pingfence@gmail.com",
      description: "Get help with your account, courses, or technical issues"
    },
    {
      icon: PhotoIcon,
      title: "Instagram",
      details: "@PINGFENCE",
      description: "Follow us for updates, tips, and community discussions"
    },
    {
      icon: MapPinIcon,
      title: "Office Address",
      details: "Mumbai, Maharashtra, India",
      description: "Visit our office for in-person consultations and meetings"
    },
    {
      icon: ClockIcon,
      title: "Business Hours",
      details: "Mon - Fri: 9:00 AM - 6:00 PM IST",
      description: "We're here to help during standard business hours"
    }
  ];

  const supportCategories = [
    {
      icon: AcademicCapIcon,
      title: "Course Support",
      description: "Help with course content, assignments, and learning materials"
    },
    {
      icon: CogIcon,
      title: "Technical Support",
      description: "Assistance with platform access, downloads, and technical issues"
    },

    {
      icon: ChatBubbleLeftRightIcon,
      title: "General Inquiries",
      description: "General questions about our services and offerings"
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="py-20 relative overflow-hidden futuristic-panel">
        <div className="absolute top-0 left-0 w-full h-full opacity-10 bg-grid-pattern"></div>
        <div className="absolute inset-0 bg-gradient-to-br from-primary/20 via-transparent to-secondary/20 opacity-70"></div>
        <div className="container relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass-effect mb-6">
              <EnvelopeIcon className="h-5 w-5 text-primary" />
              <span className="text-sm">Get in Touch</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              We're Here to <span className="text-gradient">Help</span>
            </h1>
            <p className="text-xl text-gray-300 mb-8 leading-relaxed">
              Have questions about our digital assets, courses, or services? Our support team is ready to assist you 
              with any inquiries or technical issues you may have.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Information */}
      <section className="py-16 bg-secondary">
        <div className="container">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {contactInfo.map((info, index) => (
              <div key={index} className="text-center p-6 rounded-xl bg-secondary border border-primary/10">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-primary/10 rounded-full mb-4">
                  <info.icon className="h-8 w-8 text-primary" />
                </div>
                <h3 className="text-xl font-bold mb-2">{info.title}</h3>
                <p className="text-primary font-medium mb-2">{info.details}</p>
                <p className="text-gray-800 text-sm">{info.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form & Support Categories */}
      <section className="py-20">
        <div className="container">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div>
              <h2 className="text-4xl font-bold mb-6">
                Send Us a <span className="text-gradient">Message</span>
              </h2>
              <p className="text-lg text-gray-300 mb-8">
                Fill out the form below and we'll get back to you as soon as possible.
              </p>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-300 mb-2">
                      Full Name
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 bg-secondary border border-primary/20 rounded-lg text-black placeholder-gray-400 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-colors"
                      placeholder="Enter your full name"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                      Email Address
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 bg-secondary border border-primary/20 rounded-lg text-black placeholder-gray-400 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-colors"
                      placeholder="Enter your email address"
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="subject" className="block text-sm font-medium text-gray-300 mb-2">
                    Subject
                  </label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 bg-secondary border border-primary/20 rounded-lg text-black placeholder-gray-400 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-colors"
                    placeholder="What is this regarding?"
                  />
                </div>
                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-300 mb-2">
                    Message
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    rows={6}
                    className="w-full px-4 py-3 bg-secondary border border-primary/20 rounded-lg text-black placeholder-gray-400 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-colors resize-none"
                    placeholder="Tell us more about your inquiry..."
                  />
                </div>
                <button
                  type="submit"
                  className="w-full btn btn-primary text-lg py-3"
                >
                  Send Message
                </button>
              </form>
            </div>

            {/* Support Categories */}
            <div>
              <h2 className="text-4xl font-bold mb-6">
                How Can We <span className="text-gradient">Help?</span>
              </h2>
              <p className="text-lg text-gray-300 mb-8">
                Choose the category that best describes your inquiry for faster assistance.
              </p>
              <div className="space-y-4">
                {supportCategories.map((category, index) => (
                  <div key={index} className="p-6 rounded-xl bg-secondary border border-primary/10 hover:border-primary/30 transition-colors cursor-pointer">
                    <div className="flex items-start gap-4">
                      <div className="inline-flex items-center justify-center w-12 h-12 bg-primary/10 rounded-lg flex-shrink-0">
                        <category.icon className="h-6 w-6 text-primary" />
                      </div>
                      <div>
                        <h3 className="text-lg font-bold mb-2">{category.title}</h3>
                        <p className="text-gray-800">{category.description}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-secondary">
        <div className="container">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">
              Frequently Asked <span className="text-gradient">Questions</span>
            </h2>
            <p className="text-lg text-gray-800 max-w-2xl mx-auto">
              Find quick answers to common questions about our services
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <div className="space-y-6">
              <div className="p-6 rounded-xl bg-secondary border border-primary/10">
                <h3 className="text-lg font-bold mb-2">How do I access my purchased digital assets?</h3>
                <p className="text-gray-800">After purchase, you'll receive an email with download links and access instructions for your digital assets.</p>
              </div>
              <div className="p-6 rounded-xl bg-secondary border border-primary/10">
                <h3 className="text-lg font-bold mb-2">What payment methods do you accept?</h3>
                <p className="text-gray-800">We accept all major credit cards, debit cards, UPI, and net banking through our secure payment gateway.</p>
              </div>
              <div className="p-6 rounded-xl bg-secondary border border-primary/10">
                <h3 className="text-lg font-bold mb-2">Do you offer refunds?</h3>
                <p className="text-gray-800">Yes, we offer a 30-day money-back guarantee for all our courses and digital products.</p>
              </div>
            </div>
            <div className="space-y-6">
              <div className="p-6 rounded-xl bg-secondary border border-primary/10">
                <h3 className="text-lg font-bold mb-2">How long do I have access to digital assets?</h3>
                <p className="text-gray-800">You get lifetime access to all purchased digital assets, including future updates and new content.</p>
              </div>
              <div className="p-6 rounded-xl bg-secondary border border-primary/10">
                <h3 className="text-lg font-bold mb-2">Can I download digital assets?</h3>
                <p className="text-gray-800">Yes, all digital assets including templates, scripts, and tools are available for immediate download.</p>
              </div>
              <div className="p-6 rounded-xl bg-secondary border border-primary/10">
                <h3 className="text-lg font-bold mb-2">Do you provide certificates?</h3>
                <p className="text-gray-800">Yes, you'll receive a certificate of completion for each course you finish.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20">
        <div className="container">
          <div className="text-center max-w-3xl mx-auto">
            <h2 className="text-4xl font-bold mb-6">
              Still Have <span className="text-gradient">Questions?</span>
            </h2>
            <p className="text-lg text-gray-300 mb-8">
              Our support team is available 24/7 to help you with any questions or concerns. 
              Don't hesitate to reach out!
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <button className="inline-flex items-center gap-2 btn btn-primary text-lg px-8 py-3">
                Contact Support
                <EnvelopeIcon className="h-5 w-5" />
              </button>
              <a
                href="https://ig.me/m/pingfence"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 btn btn-secondary text-lg px-8 py-3 no-underline"
              >
                Live Chat
                <ChatBubbleLeftRightIcon className="h-5 w-5" />
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact; 